import { useCallback, useEffect, useRef } from "react";

type Params = {
  scrollToBottomKey?: number;
  onAtBottomChange?: (isAtBottom: boolean) => void;
  onLoadOlder?: () => Promise<void> | void;
  isLoadingOlder?: boolean;
  hasMoreOlder?: boolean;
};

export const useChatMessagesScroll = ({
  scrollToBottomKey,
  onAtBottomChange,
  onLoadOlder,
  isLoadingOlder,
  hasMoreOlder,
}: Params) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isAtBottomRef = useRef(true);
  const loadOlderLockRef = useRef(false);
  const lastLoadOlderAtRef = useRef(0);
  const prevScrollTopRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (scrollToBottomKey == null) return;
    scrollToBottom();
  }, [scrollToBottom, scrollToBottomKey]);

  const handleScroll = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;

    const prevTop = prevScrollTopRef.current;
    const isScrollingUp = el.scrollTop < prevTop;
    prevScrollTopRef.current = el.scrollTop;

    const bottomThreshold = 60;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - bottomThreshold;
    if (isAtBottomRef.current !== isAtBottom) {
      isAtBottomRef.current = isAtBottom;
      onAtBottomChange?.(isAtBottom);
    }

    const topThreshold = 30;
    if (
      onLoadOlder &&
      hasMoreOlder !== false &&
      !isLoadingOlder &&
      !loadOlderLockRef.current &&
      isScrollingUp &&
      el.scrollTop <= topThreshold
    ) {
      const now = Date.now();
      if (now - lastLoadOlderAtRef.current < 800) return;
      lastLoadOlderAtRef.current = now;

      loadOlderLockRef.current = true;
      const prevScrollHeight = el.scrollHeight;
      const prevScrollTop = el.scrollTop;
      try {
        await onLoadOlder();
      } finally {
        requestAnimationFrame(() => {
          const nextEl = containerRef.current;
          if (!nextEl) return;
          const nextScrollHeight = nextEl.scrollHeight;
          nextEl.scrollTop = prevScrollTop + (nextScrollHeight - prevScrollHeight);
          loadOlderLockRef.current = false;
        });
      }
    }
  }, [hasMoreOlder, isLoadingOlder, onAtBottomChange, onLoadOlder]);

  return {
    containerRef,
    handleScroll,
    scrollToBottom,
  };
};
